import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  TrendingUp,
  Plus,
  Eye,
  MessageSquare,
  LogOut,
  Settings,
  BarChart3,
  Users,
  Activity,
  Flame,
  FileText,
  User,
  ChevronDown,
  Sparkles,
} from "lucide-react";
import NLQInterface from "../components/NLQInterface";
import AnalyticsDashboard from "../components/AnalyticsDashboard";
import LiveUsers from "../components/LiveUsers";
import HeatmapView from "../components/HeatmapView";
import { ThemeToggle } from '../components/ThemeToggle';
import PageSEO from '../components/PageSEO';


export default function Dashboard({ user, onLogout }) {
  const displayName = (user?.first_name?.trim() || user?.name?.trim() || 'User').split(/\s+/)[0];
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [activeTab, setActiveTab] = useState('analytics');
  const [loading, setLoading] = useState(true);
  const [newProject, setNewProject] = useState({ name: "", domain: "" });
  const [creatingProject, setCreatingProject] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Handle click outside user menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get("/projects");
      setProjects(response.data);
      if (response.data.length > 0) {
        const queryId = searchParams.get("projectId");
        const savedId = queryId || localStorage.getItem('selected_project_id');
        const found = savedId ? response.data.find(p => p.id === savedId) : null;
        const initial = found || response.data[0];
        setSelectedProject(initial);
        if (initial?.id) {
          localStorage.setItem('selected_project_id', initial.id);
        }
      }
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProject = (project) => {
    if (!project) return;
    setSelectedProject(project);
    localStorage.setItem('selected_project_id', project.id);
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (creatingProject) return;
    const name = newProject.name.trim().replace(/\s+/g, ' ');
    const domain = newProject.domain.trim().toLowerCase();
    const labels = domain.split('.');
    if (!name) return toast.error('Project name is required.');
    if (labels.length < 2 || domain.length > 253 ||
        labels.some(label => !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(label)) ||
        !/^(?:[a-z]{2,63}|xn--[a-z0-9-]{2,59})$/.test(labels[labels.length - 1])) {
      return toast.error('Enter a domain such as example.com, without https://, paths, or spaces.');
    }
    if (projects.some(project => project.name.trim().replace(/\s+/g, ' ').toLowerCase() === name.toLowerCase())) {
      return toast.error('You already have a project with this name. Choose a different name.');
    }
    setCreatingProject(true);
    try {
      const response = await axios.post("/projects", { name, domain });
      toast.success("Project created successfully!");
      setProjects([...projects, response.data]);
      setSelectedProject(response.data);
      setShowCreateProject(false);
      setNewProject({ name: "", domain: "" });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(item => item.msg).join(' ') : 'Failed to create project');
    } finally {
      setCreatingProject(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-background transition-colors flex items-center justify-center">
        <PageSEO title="Dashboard | AI Search Advisers" isPrivate={true} />
        <div className="flex flex-col items-center justify-center">
          <div className="relative flex items-center justify-center w-24 h-24 mb-6">
            <div className="absolute inset-0 rounded-full border-t-4 border-r-4 border-[#FEAB3D]/80 dark:border-[#FEAB3D] animate-spin"></div>
            <div className="absolute inset-3 rounded-full border-b-4 border-l-4 border-[#021426]/80 dark:border-slate-400 animate-[spin_1.5s_linear_infinite_reverse]"></div>
            <TrendingUp className="w-8 h-8 text-[#021426] dark:text-[#FEAB3D] animate-pulse" />
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-2xl font-black tracking-tight text-[#021426] dark:text-white">
              AI Search <span className="text-[#FEAB3D]">Advisor</span>
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold tracking-[0.2em] uppercase animate-pulse">
            Preparing Dashboard
          </p>
        </div>
      </div>
    );
  }

  if (!user?.is_premium) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-background dark:to-slate-900 transition-colors flex flex-col items-center justify-center p-6">
        <PageSEO title="Dashboard | AI Search Advisers" isPrivate={true} />
        <Card className="max-w-md w-full p-8 text-center bg-white dark:bg-card border-slate-200 dark:border-border shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#FEAB3D]/20 rounded-full blur-2xl -mr-10 -mt-10"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#021426]/10 rounded-full blur-2xl -ml-10 -mb-10"></div>
          
          <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6 relative z-10">
             <BarChart3 className="w-8 h-8 text-[#021426] dark:text-[#FEAB3D]" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 relative z-10">Upgrade to Premium</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-8 relative z-10">
            You need a premium plan to access the Analytics Dashboard and start tracking your websites.
            You can still generate free Website Intelligence Reports.
          </p>
          <div className="flex flex-col gap-4 relative z-10">
             <Button onClick={() => navigate('/pricing')} className="w-full bg-[#FEAB3D] text-[#021426] hover:bg-[#e8960a] font-bold py-6 text-lg rounded-xl">
               View Pricing Plans
             </Button>
             <Button onClick={() => navigate('/scan')} variant="outline" className="w-full py-6 text-lg rounded-xl border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800">
               Run Free Website Scan
             </Button>
             <Button onClick={onLogout} variant="ghost" className="w-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 mt-2">
               Sign Out
             </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background transition-colors">
      <PageSEO title="Dashboard | AI Search Advisers" isPrivate={true} />
      {/* Dashboard Custom Header */}
            <header className="flex flex-wrap xl:flex-nowrap items-center justify-between xl:justify-start gap-y-3 px-4 md:px-6 py-3 lg:py-4 bg-white dark:bg-card border-b border-slate-200 dark:border-border">
        
        {/* 1. Logo (Desktop: Left | Mobile: Row 1 Left) */}
        <div className="order-1 flex items-center gap-2 cursor-pointer shrink-0" onClick={() => navigate('/')}>
          <div className="w-7 h-7 lg:w-8 lg:h-8 bg-gradient-to-br from-[#FEAB3D] to-[#e8960a] rounded-lg flex items-center justify-center shadow-sm">
            <Sparkles className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-[#021426]" />
          </div>
          <span className="text-base md:text-lg lg:text-xl font-black tracking-tight text-[#021426] dark:text-white">
            AI Search <span className="text-[#FEAB3D]">Advisor</span>
          </span>
        </div>
        
        {/* 2. Live Users (Desktop: Left | Mobile: Row 1 Right) */}
        <div className="order-2 xl:order-3 shrink-0 xl:ml-4 lg:ml-6">
          <LiveUsers projectId={selectedProject?.id} />
        </div>

        {/* Mobile Break 1 */}
        <div className="order-3 basis-full xl:hidden"></div>

        {/* 3. Project Selector (Desktop: Left | Mobile: Row 2 Left) */}
        {projects.length > 0 ? (
          <select 
            aria-label="Select project"
            className="order-4 xl:order-2 flex-1 xl:flex-none mr-3 xl:mr-0 xl:ml-4 lg:ml-6 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-md px-2 md:px-3 py-1.5 text-xs md:text-sm outline-none focus:ring-1 focus:ring-[#FEAB3D] min-h-11 min-w-0 truncate xl:w-48"
            value={selectedProject?.id || ''}
            onChange={(e) => handleSelectProject(projects.find(p => p.id === e.target.value))}
          >
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        ) : (
          <div className="order-4 xl:order-2 flex-1 xl:flex-none mr-3 xl:mr-0 xl:ml-4 lg:ml-6"></div>
        )}

        {/* 4. ThemeToggle (Desktop: Right | Mobile: Row 2 Right) */}
        <div className="order-5 xl:order-5 shrink-0 xl:ml-3">
          <ThemeToggle />
        </div>

        {/* Mobile Break 2 */}
        <div className="order-6 basis-full xl:hidden"></div>

        {/* 5. Audit Button (Desktop: Right | Mobile: Row 3 Left) */}
        <button
          onClick={() => navigate('/adviser')}
          className="order-7 xl:order-4 flex-1 xl:flex-none mr-3 xl:mr-0 xl:ml-auto relative inline-flex h-11 overflow-hidden rounded-full p-[2px] focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-50 transition-transform hover:scale-105 active:scale-95 group"
        >
          <span className="absolute inset-[-1000%] animate-[spin_3s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#4285f4_0%,#34a853_25%,#fbbc05_50%,#ea4335_75%,#4285f4_100%)] opacity-80 group-hover:opacity-100 transition-opacity" />
          <span className="inline-flex h-full w-full cursor-pointer items-center justify-center rounded-full bg-white dark:bg-slate-900 px-3 lg:px-4 py-1 text-xs lg:text-sm font-semibold text-slate-800 dark:text-slate-100 backdrop-blur-3xl gap-2 z-10 whitespace-nowrap">
            <Sparkles className="w-4 h-4 text-[#4285f4] shrink-0" /> <span className="truncate">Audit your Website</span>
          </span>
        </button>

        {/* 6. New Project (Desktop: Right | Mobile: Row 3 Right) */}
        <Button 
          onClick={() => setShowCreateProject(true)} 
          variant="outline" 
          className="order-8 xl:order-6 flex-1 xl:flex-none xl:ml-3 rounded-lg border-[#FEAB3D] text-[#FEAB3D] dark:text-[#FEAB3D] hover:bg-[#FEAB3D]/10 gap-2 h-11 px-3 lg:px-4 py-2 text-xs lg:text-sm font-medium whitespace-nowrap"
        >
          <Plus className="w-4 h-4 text-[#FEAB3D] shrink-0" /> <span className="truncate">New Project</span>
        </Button>

        {/* Mobile Break 3 */}
        <div className="order-9 basis-full xl:hidden"></div>

        {/* 7. User Pill (Desktop: Right | Mobile: Row 4 Full Width) */}
        <div className="order-10 xl:order-7 w-full xl:w-auto xl:ml-3 relative" ref={userMenuRef}>
          <button
            type="button"
            aria-label="Account menu"
            aria-expanded={userMenuOpen}
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="w-full flex min-h-11 items-center justify-between xl:justify-start gap-2.5 bg-slate-100 dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700 rounded-xl px-3.5 py-1.5 cursor-pointer hover:bg-slate-200/60 dark:hover:bg-slate-700 transition-colors select-none"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-6 h-6 rounded-full bg-[#FEAB3D] text-[#021426] font-bold text-xs flex items-center justify-center shadow-sm shrink-0">
                {displayName[0].toUpperCase()}
              </div>
              <span className="truncate text-sm font-semibold text-slate-800 dark:text-slate-200">
                {displayName}
              </span>
            </div>
            <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-150 shrink-0 ${userMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Popover Dropdown Menu */}
          {userMenuOpen && (
            <div className="absolute right-0 left-0 xl:left-auto mt-2 w-full xl:w-52 bg-white dark:bg-[#061727] border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-2 z-50 animate-in fade-in zoom-in-95 duration-150">
              {/* User Settings */}
              <button
                type="button"
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/user-settings');
                }}
                className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors font-medium text-sm text-left"
              >
                <User className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                <span>User settings</span>
              </button>

              {/* Divider */}
              <div className="my-1 border-t border-slate-100 dark:border-slate-800" />

              {/* Log Out */}
              <button
                type="button"
                onClick={() => {
                  setUserMenuOpen(false);
                  onLogout();
                }}
                className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors font-medium text-sm text-left"
              >
                <LogOut className="w-4 h-4 text-red-500 dark:text-red-400" />
                <span>Log out</span>
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="px-3 md:px-5 lg:px-6 py-4 md:py-6 lg:py-8">
        {projects.length === 0 ? (
          // Empty State
          <div
            className="max-w-2xl mx-auto text-center py-20"
            data-testid="empty-state"
          >
            <div className="w-20 h-20 bg-[#FEAB3D]/20 dark:bg-[#FEAB3D]/40 rounded-full flex items-center justify-center mx-auto mb-6">
              <BarChart3 className="w-10 h-10 text-[#021426] dark:text-[#FEAB3D]" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">
              Create your first project
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">
              Start tracking analytics by creating a project for your website
            </p>
            <Button
              data-testid="create-first-project-button"
              onClick={() => setShowCreateProject(true)}
              className="bg-[#FEAB3D] text-[#021426] px-8 py-6 text-lg rounded-full hover:bg-[#e8960a] hover:text-[#021426]"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Project
            </Button>
          </div>
        ) : (
          <div>
            {/* Action Buttons */}
            <div className="flex overflow-x-auto md:flex-row gap-2 md:gap-4 mb-4 md:mb-8 pb-2 md:pb-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden items-stretch">
              <Button
                data-testid="view-analytics-button"
                onClick={() => setActiveTab('analytics')}
                className={`shrink-0 md:shrink md:flex-1 min-h-11 py-3 px-4 md:py-6 rounded-xl font-semibold transition-all whitespace-nowrap md:whitespace-normal text-sm md:text-base ${activeTab === 'analytics'
                  ? "bg-[#FEAB3D] text-[#021426] hover:bg-[#e8960a] hover:text-[#021426]"
                  : "bg-white dark:bg-card text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-700 hover:bg-[#e8960a] hover:text-[#021426] dark:hover:bg-slate-800 dark:hover:text-white"
                  }`}
              >
                <BarChart3 className="w-4 h-4 md:w-5 md:h-5 mr-1.5 md:mr-2" />
                <span className="md:hidden lg:inline">Analytics Dashboard</span>
                <span className="hidden md:inline lg:hidden">Analytics</span>
              </Button>
              <Button
                data-testid="view-heatmaps-button"
                onClick={() => setActiveTab('heatmaps')}
                className={`shrink-0 md:shrink md:flex-1 min-h-11 py-3 px-4 md:py-6 rounded-xl font-semibold transition-all whitespace-nowrap md:whitespace-normal text-sm md:text-base ${activeTab === 'heatmaps'
                  ? "bg-[#FEAB3D] text-[#021426] hover:bg-[#e8960a] hover:text-[#021426]"
                  : "bg-white dark:bg-card text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-700 hover:bg-[#e8960a] hover:text-[#021426] dark:hover:bg-slate-800 dark:hover:text-white"
                  }`}
              >
                <Flame className="w-4 h-4 md:w-5 md:h-5 mr-1.5 md:mr-2" />
                Heatmaps
              </Button>
              <Button
                data-testid="ask-nlq-button"
                onClick={() => setActiveTab('nlq')}
                className={`shrink-0 md:shrink md:flex-1 min-h-11 py-3 px-4 md:py-6 rounded-xl font-semibold transition-all whitespace-nowrap md:whitespace-normal text-sm md:text-base ${activeTab === 'nlq'
                  ? "bg-[#FEAB3D] text-[#021426] hover:bg-[#e8960a] hover:text-[#021426]"
                  : "bg-white dark:bg-card text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-700 hover:bg-[#e8960a] hover:text-[#021426] dark:hover:bg-slate-800 dark:hover:text-white"
                  }`}
              >
                <MessageSquare className="w-4 h-4 md:w-5 md:h-5 mr-1.5 md:mr-2" />
                <span className="md:hidden lg:inline">Ask Natural Language</span>
                <span className="hidden md:inline lg:hidden">NLQ</span>
              </Button>
              <Button
                data-testid="generate-report-button"
                onClick={() =>
                  navigate(`/projects/${selectedProject.id}/report`)
                }
                variant="outline"
                className="shrink-0 min-h-11 px-4 md:px-6 py-3 md:py-6 rounded-xl border-[#021426] dark:border-[#FEAB3D] text-[#021426] dark:text-[#FEAB3D] bg-white dark:bg-card hover:bg-[#f0fdf4] dark:hover:bg-[#FEAB3D]/10 font-semibold text-sm md:text-base whitespace-nowrap md:whitespace-normal"
              >
                <FileText className="w-4 h-4 md:w-5 md:h-5 mr-1.5 md:mr-2" />
                <span className="md:hidden lg:inline">Generate Report</span>
                <span className="hidden md:inline lg:hidden">Report</span>
              </Button>
              <Button
                data-testid="project-settings-button"
                onClick={() =>
                  navigate(`/projects/${selectedProject.id}/settings`)
                }
                variant="outline"
                className="shrink-0 min-h-11 px-4 md:px-6 py-3 md:py-6 rounded-xl border-slate-300 dark:border-slate-700 bg-white dark:bg-card text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 font-semibold text-sm md:text-base whitespace-nowrap md:whitespace-normal"
              >
                <Settings className="w-4 h-4 md:w-5 md:h-5 mr-1.5 md:mr-2" />
                <span className="md:hidden lg:inline">Settings</span>
                <span className="hidden md:inline lg:hidden">Config</span>
              </Button>
            </div>
            
            {/* Content Area */}
            <div className="bg-slate-50 dark:bg-background transition-colors rounded-3xl -mx-3 px-3 md:-mx-5 md:px-5 lg:-mx-6 lg:px-6 -mb-4 pb-4 md:-mb-6 md:pb-6 lg:-mb-8 lg:pb-8">
              {activeTab === 'analytics' && <AnalyticsDashboard projectId={selectedProject.id} />}
              {activeTab === 'heatmaps' && <HeatmapView projectId={selectedProject.id} />}
              {activeTab === 'nlq' && <NLQInterface projectId={selectedProject.id} />}
            </div>
          </div>
        )}
      </div>

      {/* Create Project Modal */}
      {showCreateProject && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card
            className="w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto bg-white dark:bg-card border-slate-200 dark:border-border rounded-2xl shadow-2xl p-4 sm:p-8"
            data-testid="create-project-modal"
          >
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
              Create New Project
            </h3>
            <form onSubmit={handleCreateProject} className="space-y-4">
              <div>
                <Label htmlFor="project-name" className="text-slate-700 dark:text-slate-300">
                  Project Name
                </Label>
                <Input
                  id="project-name"
                  maxLength={100}
                  data-testid="input-project-name"
                  type="text"
                  value={newProject.name}
                  onChange={(e) =>
                    setNewProject({ ...newProject, name: e.target.value })
                  }
                  required
                  placeholder="My Awesome Website"
                  className="mt-1 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                />
              </div>
              <div>
                <Label htmlFor="project-domain" className="text-slate-700 dark:text-slate-300">
                  Domain
                </Label>
                <Input
                  id="project-domain"
                  maxLength={253}
                  aria-describedby="project-domain-help"
                  data-testid="input-project-domain"
                  type="text"
                  value={newProject.domain}
                  onChange={(e) =>
                    setNewProject({ ...newProject, domain: e.target.value })
                  }
                  required
                  placeholder="example.com"
                  className="mt-1 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                />
                <p id="project-domain-help" className="mt-1 text-xs text-slate-500 dark:text-slate-400">Domain only, for example example.com or shop.example.com. No https:// or page paths.</p>
              </div>
              <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
                <Button
                  data-testid="cancel-create-project-button"
                  type="button"
                  onClick={() => setShowCreateProject(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="confirm-create-project-button"
                  disabled={creatingProject}
                  type="submit"
                  className="flex-1 bg-[#FEAB3D] text-[#021426] hover:bg-[#e8960a] hover:text-[#021426]"
                >
                  {creatingProject ? 'Creating…' : 'Create Project'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
